import React from "react";
import { COMPONENT_TRANSFORMER } from "./ComponentTransformer/index";
import { MYPARTNER_COMPONENT_MAPPER } from "./MyPartnerComponentMapper";

/**
 * Generic component card interface that matches the structure from API
 */
export interface ComponentCard {
  id: string;
  data: unknown;
}

/**
 * Props for the ComponentRenderer
 */
export interface ComponentRendererProps {
  components?: ComponentCard[];
  layout: "top" | "left" | "right";
  csrfToken?: string;
}

/**
 * Reusable component renderer that handles the mapping and transformation logic
 * This eliminates the repeated code in top, left, and right sections
 */
export const ComponentRenderer: React.FC<ComponentRendererProps> = ({
  components,
  layout,
  csrfToken,
}) => {
  if (!components) {
    return null;
  }

  return (
    <>
      {components.map((card, index) => {
        const CardComponent = MYPARTNER_COMPONENT_MAPPER[layout][card.id];
        const transformer = COMPONENT_TRANSFORMER[card.id];

        if (CardComponent) {
          // Generate a more unique key using component ID and index
          const key = `${card.id}-${index}`;

          if (transformer) {
            // Use transformer and spread transformed data as props
            const transformedData = transformer(card.data);
            // Type check before spreading
            if (transformedData && typeof transformedData === "object") {
              return (
                <CardComponent
                  key={key}
                  {...transformedData}
                  csrfToken={csrfToken}
                />
              );
            }
            return (
              <CardComponent
                key={key}
                data={transformedData}
                csrfToken={csrfToken}
              />
            );
          } else {
            // No transformer, spread props directly
            // Type check before spreading
            if (card.data && typeof card.data === "object") {
              return (
                <CardComponent key={key} {...card.data} csrfToken={csrfToken} />
              );
            }
            return (
              <CardComponent key={key} data={card.data} csrfToken={csrfToken} />
            );
          }
        }
        return null;
      })}
    </>
  );
};

export default ComponentRenderer;
